/**
 * Derived party tie-breakers: rank equal category scores using picks
 * people already made. No extra vote, no official number to enter.
 */

export const TIE_BREAKER_MODES = [
  "streak",
  "hard_categories",
  "close_of_night",
  "none",
] as const;

export type TieBreakerMode = (typeof TIE_BREAKER_MODES)[number];

export const DEFAULT_TIE_BREAKER_MODE: TieBreakerMode = "streak";
export const CLOSE_OF_NIGHT_COUNT = 5;

export interface TieBreakerCategoryInput {
  id: string;
  orderIndex: number;
  resultNomineeIds: readonly string[];
  announcedAt: Date | string | null;
}

export interface TieBreakerMetricInput {
  mode: TieBreakerMode;
  categories: readonly TieBreakerCategoryInput[];
  votesByUserId: Record<string, Record<string, string>>;
  userId: string;
  participantUserIds: readonly string[];
}

export interface RankableLeaderboardEntry {
  userId: string;
  correctCount: number;
}

export interface TieBreakerSummary {
  mode: TieBreakerMode;
  metric: number;
  hardTotal: number;
  closeTotal: number;
}

export interface TieBreakerOwnerGuidance {
  canEdit: boolean;
  message: string;
}

export const TIE_BREAKER_MODE_OPTIONS: Array<{
  value: TieBreakerMode;
  label: string;
  description: string;
}> = [
  {
    value: "streak",
    label: "Longest streak",
    description:
      "Whoever stayed correct the longest as winners were announced ranks higher.",
  },
  {
    value: "hard_categories",
    label: "Hardest categories",
    description:
      "Whoever got more of the categories fewest people in this party nailed ranks higher.",
  },
  {
    value: "close_of_night",
    label: "Close of the night",
    description:
      "Whoever got more of the last announced categories right ranks higher.",
  },
  {
    value: "none",
    label: "Leave ties",
    description: "People with the same score stay tied.",
  },
];

export function parseTieBreakerMode(value: unknown): TieBreakerMode {
  if (typeof value === "string" && TIE_BREAKER_MODES.includes(value as TieBreakerMode)) {
    return value as TieBreakerMode;
  }
  return DEFAULT_TIE_BREAKER_MODE;
}

function announcedTimeMs(value: Date | string | null): number {
  if (!value) {
    return Number.POSITIVE_INFINITY;
  }
  return value instanceof Date ? value.getTime() : new Date(value).getTime();
}

export function getAnnouncedCategoriesInOrder(
  categories: readonly TieBreakerCategoryInput[],
): TieBreakerCategoryInput[] {
  return categories
    .filter((category) => category.resultNomineeIds.length > 0)
    .sort((left, right) => {
      const timeDiff =
        announcedTimeMs(left.announcedAt) - announcedTimeMs(right.announcedAt);
      if (timeDiff !== 0) {
        return timeDiff;
      }
      if (left.orderIndex !== right.orderIndex) {
        return left.orderIndex - right.orderIndex;
      }
      return left.id.localeCompare(right.id);
    });
}

function isCorrectPick(
  category: TieBreakerCategoryInput,
  nomineeId: string | undefined,
): boolean {
  if (!nomineeId) {
    return false;
  }
  return category.resultNomineeIds.includes(nomineeId);
}

function userVotes(
  votesByUserId: Record<string, Record<string, string>>,
  userId: string,
): Record<string, string> {
  return votesByUserId[userId] ?? {};
}

function computeStreak(
  announced: readonly TieBreakerCategoryInput[],
  votes: Record<string, string>,
): number {
  let current = 0;
  let best = 0;
  for (const category of announced) {
    if (isCorrectPick(category, votes[category.id])) {
      current += 1;
      best = Math.max(best, current);
    } else {
      current = 0;
    }
  }
  return best;
}

function hardCategoryIds(
  announced: readonly TieBreakerCategoryInput[],
  votesByUserId: Record<string, Record<string, string>>,
  participantUserIds: readonly string[],
): string[] {
  if (announced.length === 0) {
    return [];
  }

  const scored = announced.map((category) => {
    const hitCount = participantUserIds.reduce((count, userId) => {
      const votes = userVotes(votesByUserId, userId);
      return isCorrectPick(category, votes[category.id]) ? count + 1 : count;
    }, 0);
    return { id: category.id, hitCount };
  });

  scored.sort((left, right) => {
    if (left.hitCount !== right.hitCount) {
      return left.hitCount - right.hitCount;
    }
    const leftIndex = announced.findIndex((category) => category.id === left.id);
    const rightIndex = announced.findIndex((category) => category.id === right.id);
    return leftIndex - rightIndex;
  });

  const take = Math.max(1, Math.ceil(announced.length / 2));
  return scored.slice(0, take).map((item) => item.id);
}

function computeHardCategoryHits(
  announced: readonly TieBreakerCategoryInput[],
  votesByUserId: Record<string, Record<string, string>>,
  participantUserIds: readonly string[],
  userId: string,
): { hits: number; total: number } {
  const hardIds = hardCategoryIds(announced, votesByUserId, participantUserIds);
  const votes = userVotes(votesByUserId, userId);
  const hardCategories = announced.filter((category) => hardIds.includes(category.id));
  const hits = hardCategories.reduce(
    (count, category) =>
      isCorrectPick(category, votes[category.id]) ? count + 1 : count,
    0,
  );
  return { hits, total: hardCategories.length };
}

function closeOfNightCategories(
  announced: readonly TieBreakerCategoryInput[],
): TieBreakerCategoryInput[] {
  if (announced.length === 0) {
    return [];
  }
  return announced.slice(-Math.min(CLOSE_OF_NIGHT_COUNT, announced.length));
}

function computeCloseOfNightHits(
  announced: readonly TieBreakerCategoryInput[],
  votes: Record<string, string>,
): { hits: number; total: number } {
  const closing = closeOfNightCategories(announced);
  const hits = closing.reduce(
    (count, category) =>
      isCorrectPick(category, votes[category.id]) ? count + 1 : count,
    0,
  );
  return { hits, total: closing.length };
}

export function computeTieBreakerSummary(
  input: TieBreakerMetricInput,
): TieBreakerSummary {
  const announced = getAnnouncedCategoriesInOrder(input.categories);
  const votes = userVotes(input.votesByUserId, input.userId);

  if (input.mode === "none" || announced.length === 0) {
    return {
      mode: input.mode,
      metric: 0,
      hardTotal: 0,
      closeTotal: 0,
    };
  }

  if (input.mode === "hard_categories") {
    const hard = computeHardCategoryHits(
      announced,
      input.votesByUserId,
      input.participantUserIds,
      input.userId,
    );
    return {
      mode: input.mode,
      metric: hard.hits,
      hardTotal: hard.total,
      closeTotal: 0,
    };
  }

  if (input.mode === "close_of_night") {
    const closing = computeCloseOfNightHits(announced, votes);
    return {
      mode: input.mode,
      metric: closing.hits,
      hardTotal: 0,
      closeTotal: closing.total,
    };
  }

  return {
    mode: input.mode,
    metric: computeStreak(announced, votes),
    hardTotal: 0,
    closeTotal: 0,
  };
}

export function computeTieBreakerMetric(input: TieBreakerMetricInput): number {
  return computeTieBreakerSummary(input).metric;
}

function compareRankedEntries(
  left: RankableLeaderboardEntry & { tieBreakerMetric: number },
  right: RankableLeaderboardEntry & { tieBreakerMetric: number },
): number {
  if (right.correctCount !== left.correctCount) {
    return right.correctCount - left.correctCount;
  }
  if (right.tieBreakerMetric !== left.tieBreakerMetric) {
    return right.tieBreakerMetric - left.tieBreakerMetric;
  }
  return 0;
}

export function rankEntriesWithTieBreakerMode<T extends RankableLeaderboardEntry>(
  entries: T[],
  input: {
    mode: TieBreakerMode;
    categories: readonly TieBreakerCategoryInput[];
    votesByUserId: Record<string, Record<string, string>>;
    participantUserIds: readonly string[];
  },
): Array<T & { rank: number; tieBroken: boolean; tieBreakerMetric: number; tieBreakerSummary: TieBreakerSummary }> {
  const withMetrics = entries.map((entry) => {
    const summary = computeTieBreakerSummary({
      mode: input.mode,
      categories: input.categories,
      votesByUserId: input.votesByUserId,
      userId: entry.userId,
      participantUserIds: input.participantUserIds,
    });
    return {
      ...entry,
      tieBreakerMetric: summary.metric,
      tieBreakerSummary: summary,
    };
  });

  const sorted = [...withMetrics].sort((left, right) => {
    const compared = compareRankedEntries(left, right);
    if (compared !== 0) {
      return compared;
    }
    return left.userId.localeCompare(right.userId);
  });

  let previous: (typeof sorted)[number] | null = null;
  let currentRank = 0;
  const ranked = sorted.map((entry) => {
    if (previous === null || compareRankedEntries(previous, entry) !== 0) {
      currentRank += 1;
    }
    previous = entry;
    return {
      ...entry,
      rank: currentRank,
      tieBroken: false,
    };
  });

  const rankSpanByScore = new Map<number, { min: number; max: number }>();
  for (const entry of ranked) {
    const span = rankSpanByScore.get(entry.correctCount);
    if (!span) {
      rankSpanByScore.set(entry.correctCount, {
        min: entry.rank,
        max: entry.rank,
      });
    } else {
      span.min = Math.min(span.min, entry.rank);
      span.max = Math.max(span.max, entry.rank);
    }
  }

  return ranked.map((entry) => {
    const span = rankSpanByScore.get(entry.correctCount);
    const splitByTieBreaker = !!span && span.max > span.min;
    return {
      ...entry,
      tieBroken: splitByTieBreaker && entry.rank === span.min,
    };
  });
}

export function formatTieBreakerWinReason(summary: TieBreakerSummary): string {
  if (summary.mode === "hard_categories") {
    return `Won on ${summary.metric} of the ${summary.hardTotal} hardest categories`;
  }
  if (summary.mode === "close_of_night") {
    return `Won on ${summary.metric} of the last ${summary.closeTotal} announced`;
  }
  if (summary.mode === "none") {
    return "";
  }
  return `Won on a ${summary.metric}-category streak`;
}

export function getTieBreakerScoringNote(mode: TieBreakerMode): string {
  switch (mode) {
    case "hard_categories":
      return "If scores are tied, whoever got more of this party's hardest categories right ranks higher.";
    case "close_of_night":
      return "If scores are tied, whoever got more of the last announced categories right ranks higher.";
    case "none":
      return "Tied scores stay tied.";
    case "streak":
    default:
      return "If scores are tied, the longest run of correct picks as winners are announced ranks higher.";
  }
}

export function getTieBreakerBallotNote(
  mode: TieBreakerMode,
  isLocked: boolean,
): string {
  const scoring = getTieBreakerScoringNote(mode);
  if (isLocked || mode === "none") {
    return scoring;
  }
  return `${scoring} The host can change this until voting locks.`;
}

export function getTieBreakerModeLabel(mode: TieBreakerMode): string {
  return TIE_BREAKER_MODE_OPTIONS.find((option) => option.value === mode)?.label
    ?? "Longest streak";
}

export function getTieBreakerOwnerGuidance(input: {
  isLocked: boolean;
}): TieBreakerOwnerGuidance {
  if (input.isLocked) {
    return {
      canEdit: false,
      message:
        "Voting is locked, so this rule can't change. It still ranks equal scores from picks people already made.",
    };
  }

  return {
    canEdit: true,
    message:
      "If scores are tied, this ranks people from picks they already made. No extra question. It freezes when voting locks.",
  };
}

export function isPartyBallotComplete(input: {
  userVoteCount: number;
  totalCategories: number;
}): boolean {
  if (input.totalCategories <= 0) {
    return false;
  }
  return input.userVoteCount >= input.totalCategories;
}
